import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "@majourneys/icons";
import { hubCollections } from "@majourneys/shared";
import { BEVEL_CHIP_ON, BEVEL_INSET_WHITE, BEVEL_NAVY_FACE, BEVEL_WHITE_FACE, CONTROL_EDGE } from "@majourneys/shared/ui";
import { useCollectionLabels } from "../lib/hub-collections";

const NAVY = "#110E39";
const GOLD = "#E5B265";
const MUTED = "#5a5d77";
const SOFT_TINT = "rgba(17,14,57,0.055)";

/**
 * The next search after a collection chip changes: sets or drops `tag` and
 * goes back to page 1, and keeps every other choice in the URL (city, dates,
 * guests, sort, price...). In TanStack Router a navigate without `search`
 * wipes the whole query string, so the updater always starts from `prev`.
 * The page reset is deliberate: a new filter starts the list from the top.
 */
export function collectionTagSearch(prev: Record<string, unknown>, next: string | undefined) {
  const { tag: _drop, page: _page, ...rest } = prev;
  return next ? { ...rest, tag: next } : rest;
}

/**
 * The collection chips on the restaurants hub: Dinner shows, Live music,
 * Rooftops, Terrace and garden, Open late (owner, 2026-09-30, from
 * /restaurants-spas-lab R7). One chip at a time; tapping the active chip
 * clears it, and so does the trailing Clear button.
 *
 * Pewter kit #04 (owner, 2026-10-09): white control pills with one hairline,
 * the count in a soft badge, the chosen chip navy with a gold check. A chip
 * with no venue in the current list is hidden unless it is the active one, so
 * a city never offers a filter that empties the grid. Counts come from the
 * API (`tagCounts`), taken before the tag filter.
 *
 * The spas hub has its own row through the same component (owner,
 * 2026-10-25): Hammam, Couples, Men welcome, Under 300 MAD. So do the
 * restaurant cuisines (owner, 2026-10-30), passed in as `ids`.
 */
export function CollectionChips({
  category = "restaurants",
  counts,
  active,
  onChange,
  leading,
  collections = true,
  ids,
  labels,
  testId = "restaurant-collections",
}: {
  /** Whose chips: "restaurants" or "spas". */
  category?: string;
  counts: Record<string, number> | undefined;
  active: string | undefined;
  onChange: (tag: string | undefined) => void;
  /** Chips that lead the row: "Open now" and "Tables tonight" (#65). */
  leading?: ReactNode;
  /** False on hubs with no collections (nightclubs): only `leading` shows. */
  collections?: boolean;
  /** Another row of chips on the same pattern (the cuisines); defaults to the hub's collections. */
  ids?: readonly string[];
  /** The words for `ids`, and the row's group label. */
  labels?: { label: (id: string) => string; groupLabel: string };
  testId?: string;
}) {
  const { t } = useTranslation();
  const hubLabels = useCollectionLabels(category);
  const { label, groupLabel } = labels ?? hubLabels;
  const shown = collections
    ? (ids ?? hubCollections(category).map((c) => c.id))
        .filter((id) => id === active || (counts?.[id] ?? 0) > 0)
        // Collections lead with their icon (the map sheet's icons); the
        // cuisine row stays words only, so the two rows never look alike
        // (owner, 2026-11-03, option A).
        .map((id) => ({ id, icon: ids ? undefined : hubCollections(category).find((c) => c.id === id)?.icon }))
    : [];
  // A row that opens with a chip already chosen (refresh, Back, a shared
  // link) brings that chip into view: on a phone the later chips sit past the
  // edge, and the guest could not see what was picked. Sideways only, so the
  // page itself never jumps; works the same in RTL (rect deltas, not offsets).
  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const row = rowRef.current;
    const chip = row?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!row || !chip) return;
    const r = row.getBoundingClientRect();
    // Not laid out yet (hidden, or a test DOM): nothing to scroll.
    if (r.width === 0) return;
    const c = chip.getBoundingClientRect();
    const pad = 16;
    if (c.left < r.left + pad) row.scrollBy({ left: c.left - r.left - pad });
    else if (c.right > r.right - pad) row.scrollBy({ left: c.right - r.right + pad });
  }, [active]);

  if (shown.length === 0 && !leading) return null;

  return (
    <div
      ref={rowRef}
      role="group"
      aria-label={groupLabel}
      // A sideways scroller clips vertically too, so it gets room above and
      // below for the chips' hairline ring and shadow, handed back by the
      // negative margins so the layout does not move.
      className="-mx-4 -mt-2 mb-2 flex items-center gap-2 overflow-x-auto px-4 pb-4 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      data-testid={testId}
    >
      {leading}
      {shown.map((c) => {
        const on = c.id === active;
        const count = counts?.[c.id];
        return (
          <button
            key={c.id}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(on ? undefined : c.id)}
            // Colours change on a short fade; the width is never animated
            // (the check simply takes its place).
            className="mj-press inline-flex min-h-[44px] shrink-0 items-center gap-2 whitespace-nowrap rounded-full py-1.5 pe-2 ps-4 text-[14px] font-semibold outline-none transition-[background,color,box-shadow] duration-200 focus-visible:ring-2 focus-visible:ring-[#E5B265] focus-visible:ring-offset-2"
            style={
              on
                ? { background: BEVEL_NAVY_FACE, color: "#fff", boxShadow: BEVEL_CHIP_ON }
                : { background: BEVEL_WHITE_FACE, color: NAVY, boxShadow: `${BEVEL_INSET_WHITE}, 0 0 0 1px ${CONTROL_EDGE}` }
            }
          >
            {on ? (
              <Icon
                name="ri-check-line"
                size={15}
                strokeWidth={2.8}
                color={GOLD}
                className="mj-chip-check shrink-0"
                aria-hidden="true"
              />
            ) : c.icon ? (
              <Icon name={c.icon} size={15} className="shrink-0" aria-hidden="true" />
            ) : null}
            {label(c.id)}
            {count !== undefined ? (
              <span
                className="rounded-full px-2 py-0.5 text-[12px] font-semibold tabular-nums"
                style={on ? { background: "rgba(255,255,255,0.16)" } : { background: SOFT_TINT, color: MUTED }}
              >
                {count}
              </span>
            ) : null}
          </button>
        );
      })}
      {active ? (
        <button
          type="button"
          onClick={() => onChange(undefined)}
          className="inline-flex min-h-[44px] shrink-0 items-center gap-1 px-2 text-[14px] font-semibold underline-offset-2 hover:underline"
          style={{ color: NAVY }}
          data-testid={`${testId}-clear`}
        >
          <Icon name="ri-close-line" size={15} strokeWidth={2.4} aria-hidden="true" />
          {t("common.clear")}
        </button>
      ) : null}
    </div>
  );
}
